import React, { lazy, Suspense } from "react";
import { BottomSheet } from "../BottomSheet/BottomSheet";
import { SummaryModes } from "../SummaryModes/SummaryModes";
import { WebcamsList } from "../WebcamsList/WebcamsList";
import { WebcamViewer } from "../WebcamViewer/WebcamViewer";
import { Profile } from "../Profile/Profile";
import { Settings } from "../Settings/Settings";
import { FullscreenSummary } from "../FullscreenSummary/FullscreenSummary";
import { ContestGifts } from "../ContestGifts/ContestGifts";
import { RatingModal } from "../RatingModal/RatingModal";
import { NotificationsModal } from "../Notifications/NotificationsModal";
import { ModalsState } from "../../hooks/useModalsState";
import { NetworkingBanner } from "../Networking/NetworkingBanner/NetworkingBanner";
import type { City, Webcam } from "../../types";
import { CitySettings } from "../../types/map";
import { Event } from "../../types";

// Lazy loading для тяжелых компонентов
const FlowersMainPage = lazy(() =>
  import("../FlowersMainPage/FlowersMainPage").then((module) => ({
    default: module.FlowersMainPage,
  }))
);

const Networking = lazy(() =>
  import("../Networking/Networking").then((module) => ({
    default: module.Networking,
  }))
);

const FullscreenContest = lazy(() =>
  import("../FullscreenContest/FullscreenContest").then((module) => ({
    default: module.FullscreenContest,
  }))
);

const GiftSubscription = lazy(() =>
  import("../GiftSubscription/GiftSubscription").then((module) => ({
    default: module.GiftSubscription,
  }))
);

const ActivateGiftSubscription = lazy(() =>
  import("../ActivateGiftSubscription/ActivateGiftSubscription").then((module) => ({
    default: module.ActivateGiftSubscription,
  }))
);
interface ModalsRendererProps {
  modalsState: ModalsState;
  modalsActions: {
    setActiveModal: (modal: string | null) => void;
    setSelectedWebcam: (webcam: any) => void;
    setShowProfile: (show: boolean) => void;
    setShowSettings: (show: boolean) => void;
    setShowFullscreenSummary: (show: boolean) => void;
    setShowDirectSettings: (show: boolean) => void;
    setShowFullscreenContest: (show: boolean) => void;
    setShowRatingModal: (show: boolean) => void;
    setShowContestGifts: (show: boolean) => void;
    setShowFlowers: (show: boolean) => void;
    setShowNetworking: (show: boolean) => void;
    setShowNetworkingBanner: (show: boolean) => void;
    setShowNotifications: (show: boolean) => void;
    setShowGiftSubscription: (show: boolean) => void;
    setShowActivateGift: (show: boolean) => void;
    setActivateGiftCode: (code: string | null) => void;
    setNetworkingInitialTab: (tab: "feed" | "contacts" | "requests" | "profile") => void;
  };
  webcams: Webcam[];
  contestData: any;
  telegramUser: any;
  userPremium: boolean;
  onModeSelect: (modeId: string) => void;
  onZoomChange: (direction: "in" | "out") => void;
  onWebcamSelect: (webcam: Webcam) => void;
  onChangeCity: () => void;
  onCitySelect: (city: City) => void;
  onOpenMapFromSummary: () => void;
  onCloseDirectSettings: () => void;
  urlParams?: { startParam?: string | null };
  citySettings?: CitySettings;
  onLocationClarificationRequest?: (data: {
    event: Event;
    title: string;
    coordinates: [number, number];
  }) => void;
  onGiftActivationSuccess?: () => void;
}

export const ModalsRenderer: React.FC<ModalsRendererProps> = ({
  modalsState,
  modalsActions,
  webcams,
  contestData,
  telegramUser,
  userPremium,
  onModeSelect,
  onZoomChange,
  onWebcamSelect,
  onChangeCity,
  onCitySelect,
  onOpenMapFromSummary,
  onCloseDirectSettings,
  onLocationClarificationRequest,
  onGiftActivationSuccess,
}) => {
  const {
    activeModal,
    selectedWebcam,
    showProfile,
    showSettings,
    showFullscreenSummary,
    showDirectSettings,
    showFullscreenContest,
    showRatingModal,
    showContestGifts,
    showFlowers,
    showNetworking,
    showNetworkingBanner,
    showNotifications,
    showGiftSubscription,
    showActivateGift,
    activateGiftCode,
    networkingInitialTab,
  } = modalsState;

  return (
    <>
      {/* Bottom Sheets */}
      <BottomSheet
        isOpen={activeModal === "summary"}
        onClose={() => modalsActions.setActiveModal(null)}
        showBackButton={true}
        height="auto"
      >
        <SummaryModes
          onModeSelect={onModeSelect}
          onZoomChange={onZoomChange}
          onWebcamSelect={onWebcamSelect}
          onLocationClarificationRequest={onLocationClarificationRequest}
        />
      </BottomSheet>

      <BottomSheet
        isOpen={activeModal === "webcams"}
        onClose={() => modalsActions.setActiveModal(null)}
        onBack={
          selectedWebcam
            ? () => modalsActions.setSelectedWebcam(null)
            : undefined
        }
        showBackButton={true}
      >
        {selectedWebcam ? (
          <WebcamViewer
            webcam={selectedWebcam}
            onBack={() => modalsActions.setSelectedWebcam(null)}
          />
        ) : (
          <WebcamsList webcams={webcams} onWebcamSelect={onWebcamSelect} />
        )}
      </BottomSheet>

      {/* Fullscreen Modals */}
      {showFullscreenContest && contestData && (
        <Suspense fallback={null}>
          <FullscreenContest
            contestData={contestData}
            currentUserId={telegramUser?.id}
            onClose={() => modalsActions.setShowFullscreenContest(false)}
          />
        </Suspense>
      )}

      {showProfile && (
        <Profile
          onChangeCity={onChangeCity}
          isPremium={userPremium}
          onClose={() => {
            modalsActions.setShowProfile(false);
            modalsActions.setShowSettings(false);
          }}
          settingsOpen={showSettings}
          onNotificationsClick={() => {
            modalsActions.setShowProfile(false);
            modalsActions.setShowNotifications(true);
          }}
          onNetworkingClick={() => {
            modalsActions.setShowProfile(false);
            modalsActions.setNetworkingInitialTab("profile");
            modalsActions.setShowNetworking(true);
          }}
          onGiftSubscriptionClick={() => {
            modalsActions.setShowProfile(false);
            modalsActions.setShowGiftSubscription(true);
          }}
        />
      )}

      {showSettings && (
        <Settings
          onClose={() => modalsActions.setShowSettings(false)}
          onCitySelect={onCitySelect}
        />
      )}

      {showFullscreenSummary && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "var(--app-bg-color)",
            zIndex: 10000,
          }}
        >
          <FullscreenSummary
            onOpenMap={onOpenMapFromSummary}
            onLocationClarificationRequest={onLocationClarificationRequest}
          />
        </div>
      )}

      {showDirectSettings && (
        <Settings onClose={onCloseDirectSettings} onCitySelect={onCitySelect} />
      )}

      {showContestGifts && contestData && (
        <ContestGifts
          gifts={contestData.gifts || []}
          isOpen={showContestGifts}
          onClose={() => modalsActions.setShowContestGifts(false)}
        />
      )}

      <RatingModal
        isOpen={showRatingModal}
        onClose={() => modalsActions.setShowRatingModal(false)}
      />
      {showFlowers && (
        <Suspense fallback={null}>
          <FlowersMainPage
            isOpen={showFlowers}
            onClose={() => modalsActions.setShowFlowers(false)}
          />
        </Suspense>
      )}
      {showNetworking && (
        <Suspense fallback={null}>
          <Networking
            isOpen={showNetworking}
            onClose={() => {
              const shouldReturnToProfile = networkingInitialTab === "profile";
              modalsActions.setShowNetworking(false);
              modalsActions.setNetworkingInitialTab("feed");
              if (shouldReturnToProfile) {
                modalsActions.setShowProfile(true);
              }
            }}
            initialTab={networkingInitialTab}
          />
        </Suspense>
      )}
      {showNetworkingBanner && (
        <NetworkingBanner
          isOpen={showNetworkingBanner}
          onClose={() => modalsActions.setShowNetworkingBanner(false)}
          onCreateProfile={() => {
            modalsActions.setShowNetworkingBanner(false);
            modalsActions.setShowNetworking(true);
          }}
        />
      )}
      {showNotifications && (
        <NotificationsModal
          onClose={() => {
            modalsActions.setShowNotifications(false);
            modalsActions.setShowProfile(true);
          }}
        />
      )}
      {showGiftSubscription && (
        <Suspense fallback={null}>
          <GiftSubscription
            isOpen={showGiftSubscription}
            onClose={() => modalsActions.setShowGiftSubscription(false)}
          />
        </Suspense>
      )}
      {showActivateGift && !!activateGiftCode && (
        <Suspense fallback={null}>
          <ActivateGiftSubscription
            isOpen={showActivateGift && !!activateGiftCode}
            onClose={() => {
              modalsActions.setShowActivateGift(false);
              modalsActions.setActivateGiftCode(null);
            }}
            giftCode={activateGiftCode || ""}
            onActivationSuccess={onGiftActivationSuccess}
          />
        </Suspense>
      )}
    </>
  );
};
